import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import FormTemplate from "../../templates/FormTemplate";
import { useNavigation } from "../../../hooks/useNavigation";
import { PageRoute } from "../../../constants/routes";
import { LoginError } from "../../pages/login/hook";
import { validateEmail } from "../../../utils/validation";
import { useButtonState } from "../../../hooks/useButtonState";

type Props = {
  handleSubmit: (email: string, password: string, onError?: (error: LoginError) => void) => void;
  isLoading?: boolean;
};

export const LoginForm: React.FC<Props> = ({ handleSubmit, isLoading = false }) => {
  const [emailError, setEmailError] = useState<string | undefined>();
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [generalError, setGeneralError] = useState<string | undefined>();
  const { navigateToPage } = useNavigation();

  // Use button state hook to prevent double clicks
  const { isSubmitting, executeWithLoading } = useButtonState();

  const schema = useMemo(() => {
    return z.object({
      email: z.string().min(1, "Email is required").email("Please enter a valid email address"),
      password: z.string().min(1, "Password is required"),
    });
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  const handleFormSubmit = ({ email, password }: { email: string; password: string }) => {
    // Prevent submission if already submitting
    if (isSubmitting) {
      return;
    }

    // Use executeWithLoading to prevent double submissions
    executeWithLoading(async () => {
      // Clear previous errors
      setEmailError(undefined);
      setPasswordError(undefined);
      setGeneralError(undefined);

      // Client-side validation
      if (!email.trim()) {
        setEmailError("Email is required");
        throw new Error("Email is required");
      }

      if (!validateEmail(email)) {
        setEmailError("Please enter a valid email address");
        throw new Error("Please enter a valid email address");
      }

      if (!password.trim()) {
        setPasswordError("Password is required");
        throw new Error("Password is required");
      }

      if (password.length < 1) {
        setPasswordError("Password cannot be empty");
        throw new Error("Password cannot be empty");
      }

      // Wrap handleSubmit in a Promise to work with executeWithLoading
      return new Promise<void>((resolve, reject) => {
        handleSubmit(email.trim(), password, (error: LoginError) => {
          // Handle different error types
          switch (error.type) {
            case 'email':
              setEmailError(error.message);
              break;
            case 'password':
              setPasswordError(error.message);
              break;
            case 'general':
              setGeneralError(error.message);
              break;
          }
          reject(new Error(error.message));
        });

        // For successful logins, resolve the promise
        setTimeout(resolve, 100);
      });
    }).catch((error) => {
      // Error is already handled by setting error states above
      console.log('Login submission error handled:', error.message);
    });
  };

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      isSubmitting={isSubmitting}
      defaultValues={{ email: "", password: "" }}
      onSubmit={handleFormSubmit}
    >
      <StyledInputContainer>
        <Input
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          autoFocus
          hasError={!!emailError}
          errorMessage={emailError}
          fullWidth
          disabled={isLoading}
        />
      </StyledInputContainer>

      <StyledInputContainer>
        <Input
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          hasError={!!passwordError}
          errorMessage={passwordError}
          fullWidth
          disabled={isLoading}
        />
      </StyledInputContainer>

      {generalError && (
        <StyledErrorContainer>
          <StyledErrorMessage>{generalError}</StyledErrorMessage>
        </StyledErrorContainer>
      )}

      <StyledTextContainer>
        if you encounter any problem please contact admin.
      </StyledTextContainer>

      <StyledFooter>
        <Button
          variant="filled"
          text={isSubmitting || isLoading ? "Logging in..." : "Login"}
          type="submit"
          fullWidth
          disabled={isLoading}
          isSubmitting={isSubmitting}
        />
      </StyledFooter>

      <StyledLinkContainer>
        <Button
          variant="link"
          text="Forgot your password?"
          type="button"
          onClick={() => navigateToPage(PageRoute.FORGOT_PASSWORD)}
          disabled={isLoading}
        />
      </StyledLinkContainer>
    </FormTemplate>
  );
};

const StyledInputContainer = styled.div`
  margin-bottom: 16px;
`;

const StyledErrorContainer = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
`;

const StyledErrorMessage = styled.div`
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
`;

const StyledTextContainer = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: 24px;
  color: #697077;
`;

const StyledFooter = styled.div`
  margin-top: 32px;
`;

const StyledLinkContainer = styled.div`
  display: flex;
  justify-content: center;
`;
